import Modal from "@/components/common/modal";
import TextField, { TextAreaField } from "@/components/common/text-field";
import { DialogFooter } from "@/components/ui/dialog";
import { useAuthorization } from "@/hooks/use-authorization";
import { useDuplicateRole, useGetRole } from "@/queries/roles";
import { schema, type NewRoleFormData } from "@/schemas/roles/new-roles";
import { Permissions } from "@/utils/permissions";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

interface Props {
  roleId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const DuplicateModal = ({ roleId, isOpen, onClose }: Props) => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading: isLoadingRoleData } = useGetRole(roleId!);
  const { mutateAsync, isPending: isLoading } = useDuplicateRole(roleId!);
  const { hasPermission } = useAuthorization();
  const canUpdate = hasPermission(Permissions.roles.update);
  const canRead = hasPermission(Permissions.roles.read);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<NewRoleFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: data?.data.name ? `${data.data.name} (Copia)` : "",
      description: data?.data.description || "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (data?.data && isOpen && roleId === data.data.id) {
      reset({
        name: data.data.name ? `${data.data.name} (Copia)` : "",
        description: data.data.description || "",
      });
    }
  }, [data, isOpen, roleId, reset]);

  const [name, description] = watch(["name", "description"]);

  const onSubmit = async (data: NewRoleFormData) => {
    try {
      const newRole = await mutateAsync({
        ...data,
      });
      if (newRole.duplicated_role.id && canUpdate && canRead) {
        navigate(`/roles/${newRole.duplicated_role.id}`);
        onClose();
      } else {
        onClose();
      }
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      if (e.status === 409 && e.response?.data.message.includes("already")) {
        setError("name", {
          type: "manual",
          message: "El nombre del rol ya existe. Por favor, elige otro.",
        });
      }
      // Handle error here, e.g., show a notification
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      description: "",
    }); // Reset form values when closing the modal
    clearErrors(); // Clear any validation errors
    client.invalidateQueries({
      queryKey: ["role", roleId],
      exact: true,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Duplicar rol"
      description="Cambia los datos del rol duplicado para poder continuar."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
        id="edit-role-form"
      >
        <TextField
          label="Nombre"
          required
          placeholder="Nombre del rol"
          {...register("name")}
          limit={125}
          count={name?.length || 0}
          error={errors.name?.message}
          disabled={isLoadingRoleData}
        />
        <TextAreaField
          label="Descripción"
          required
          placeholder="Descripción del rol"
          className="relative h-[188px] max-h-[188px]"
          {...register("description")}
          limit={500}
          count={description?.length || 0}
          error={errors.description?.message}
          disabled={isLoadingRoleData}
        />
        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full rounded-lg"
              variant="tertiary"
              onClick={onClose}
              disabled={isLoading || isLoadingRoleData}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              className="w-full rounded-lg"
              type="submit"
              disabled={isLoading || !isValid || isLoadingRoleData}
            >
              Duplicar rol
              {isLoading && (
                <Icon
                  type="far"
                  name="spinner-third"
                  className="text-current animate-spin"
                />
              )}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default DuplicateModal;
